'use client'

import { useRouter } from 'next/router'
import Link from 'next/link'
import { ChevronRight, Sparkles } from 'lucide-react'
import { Icon } from '../Icon'
import { COMING_SOON_DEFAULTS } from '../../pages/coming-soon'
import {
    NavigationItem,
    NavigationLink,
    NavigationSection,
} from '../navigation/types'
import { Badge } from '../ui/badge'
import { XSmall } from '../ui/typography'
import { useAnalytics } from '@/hooks/useAnalytics'

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    SidebarGroup,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar'

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

// Helper to type guard navigation sections
const isNavigationSection = (
    item: NavigationItem,
): item is NavigationSection => {
    return 'items' in item && 'name' in item
}

// Helper to type guard navigation links
const isNavigationLink = (item: NavigationItem): item is NavigationLink => {
    return 'label' in item && !('items' in item)
}

// Component for standalone navigation items (like Home)
const NavStandaloneItem = ({ item }: { item: NavigationLink }) => {
    const router = useRouter()
    const currentPath = router.pathname
    const isActive = currentPath === item.href
    const { trackNavItemClick } = useAnalytics()

    const handleClick = (e: React.MouseEvent) => {
        // Track navigation click
        trackNavItemClick('main_nav', item.label || item.value)

        if (item.comingSoon) {
            e.preventDefault()
            router.push({
                pathname: '/coming-soon',
                query: {
                    title: item.label || COMING_SOON_DEFAULTS.title,
                    icon: item.icon || COMING_SOON_DEFAULTS.icon,
                    feature: item.value || COMING_SOON_DEFAULTS.feature,
                },
            })
            return
        }

        if (item.openInNewTab && item.href) {
            e.preventDefault()
            window.open(item.href, '_blank')
            return
        }
    }

    // Wrap with tooltip if coming soon
    if (item.comingSoon) {
        return (
            <SidebarMenuItem>
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <SidebarMenuButton
                                asChild
                                data-active={isActive}
                                className="truncate text-sm"
                            >
                                <Link
                                    href={item.href}
                                    passHref
                                    onClick={handleClick}
                                    className="flex w-full items-center justify-between truncate"
                                >
                                    <div className="flex items-center">
                                        <Icon
                                            type={item.icon as any}
                                            color="currentColor"
                                            width={16}
                                            height={16}
                                        />
                                        <span className="ml-2 truncate">
                                            {item.label}
                                        </span>
                                    </div>
                                    {item.comingSoon && (
                                        <Badge
                                            variant="outline"
                                            className="ml-2 h-4 shrink-0 py-0 text-xs"
                                        >
                                            <XSmall>Coming soon</XSmall>
                                        </Badge>
                                    )}
                                </Link>
                            </SidebarMenuButton>
                        </TooltipTrigger>
                        <TooltipContent>
                            {item.label} - Coming Soon
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </SidebarMenuItem>
        )
    }

    // Regular rendering without tooltip
    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                asChild
                data-active={isActive}
                className="truncate text-sm"
            >
                <Link
                    href={item.href}
                    passHref
                    onClick={handleClick}
                    className="flex w-full items-center justify-between truncate"
                >
                    <div className="flex items-center">
                        <Icon
                            type={item.icon as any}
                            color="currentColor"
                            width={16}
                            height={16}
                        />
                        <span className="ml-2 truncate">{item.label}</span>
                    </div>
                    {item.comingSoon && (
                        <Badge
                            variant="outline"
                            className="ml-2 h-4 shrink-0 py-0 text-xs"
                        >
                            <XSmall>Coming soon</XSmall>
                        </Badge>
                    )}
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    )
}

export function NavMain({ items }: { items: NavigationItem[] }) {
    const router = useRouter()
    const currentPath = router.pathname
    const { trackNavItemClick } = useAnalytics()

    const handleSubItemClick = (
        item: NavigationLink,
        e: React.MouseEvent,
        sectionName?: string,
    ) => {
        // Track navigation click
        trackNavItemClick(sectionName || 'main_nav', item.label || item.value)

        if (item.comingSoon) {
            e.preventDefault()
            router.push({
                pathname: '/coming-soon',
                query: {
                    title: item.label || COMING_SOON_DEFAULTS.title,
                    icon: item.icon || COMING_SOON_DEFAULTS.icon,
                    feature: item.value || COMING_SOON_DEFAULTS.feature,
                },
            })
            return
        }

        if (item.openInNewTab && item.href) {
            e.preventDefault()
            window.open(item.href, '_blank')
            return
        }
    }

    // Separate standalone items and section items
    const standaloneItems = items.filter(isNavigationLink)
    const sectionItems = items.filter(isNavigationSection)

    return (
        <SidebarGroup>
            <SidebarMenu>
                {/* Render standalone items first */}
                {standaloneItems.map((item) => (
                    <NavStandaloneItem key={item.value} item={item} />
                ))}

                {/* Render collapsible sections */}
                {sectionItems.map((section) => (
                    <Collapsible
                        key={section.value}
                        asChild
                        defaultOpen={section.defaultIsOpen}
                    >
                        <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                                <SidebarMenuButton className="group w-full truncate text-sm">
                                    <Icon
                                        type={section.icon as any}
                                        color="currentColor"
                                        width={16}
                                        height={16}
                                    />
                                    <span className="truncate">
                                        {section.name}
                                    </span>
                                    <ChevronRight className="ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                                </SidebarMenuButton>
                            </CollapsibleTrigger>
                            {section.items?.length > 0 && (
                                <CollapsibleContent>
                                    <SidebarMenuSub className="px-2">
                                        {section.items
                                            .filter((item) => item.enabled)
                                            .map((item) => (
                                                <SidebarMenuSubItem
                                                    key={item.value}
                                                >
                                                    {item.comingSoon ? (
                                                        <TooltipProvider
                                                            delayDuration={200}
                                                        >
                                                            <Tooltip>
                                                                <TooltipTrigger
                                                                    asChild
                                                                >
                                                                    <SidebarMenuSubButton
                                                                        asChild
                                                                        className="truncate pr-0 text-sm"
                                                                        data-active={
                                                                            currentPath ===
                                                                            item.href
                                                                        }
                                                                        data-coming-soon={
                                                                            item.comingSoon
                                                                        }
                                                                    >
                                                                        <Link
                                                                            href={
                                                                                item.href ||
                                                                                '#'
                                                                            }
                                                                            passHref
                                                                            onClick={(
                                                                                e,
                                                                            ) =>
                                                                                handleSubItemClick(
                                                                                    item,
                                                                                    e,
                                                                                    section.name,
                                                                                )
                                                                            }
                                                                            className="flex w-full items-center justify-between truncate pr-0"
                                                                        >
                                                                            <div className="flex items-center truncate">
                                                                                <span className="truncate">
                                                                                    {
                                                                                        item.label
                                                                                    }
                                                                                </span>
                                                                                {item.hasSparkles && (
                                                                                    <Sparkles className="ml-1 size-4" />
                                                                                )}
                                                                            </div>
                                                                            {item.comingSoon && (
                                                                                <Badge
                                                                                    variant="outline"
                                                                                    className="ml-2 h-4 shrink-0 py-0 text-xs"
                                                                                >
                                                                                    <XSmall>
                                                                                        Coming
                                                                                        soon
                                                                                    </XSmall>
                                                                                </Badge>
                                                                            )}
                                                                        </Link>
                                                                    </SidebarMenuSubButton>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {item.label}{' '}
                                                                    - Coming
                                                                    Soon
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    ) : (
                                                        <SidebarMenuSubButton
                                                            asChild
                                                            className="truncate pr-0 text-sm"
                                                            data-active={
                                                                currentPath ===
                                                                item.href
                                                            }
                                                            data-coming-soon={
                                                                item.comingSoon
                                                            }
                                                        >
                                                            <Link
                                                                href={
                                                                    item.href ||
                                                                    '#'
                                                                }
                                                                passHref
                                                                onClick={(e) =>
                                                                    handleSubItemClick(
                                                                        item,
                                                                        e,
                                                                        section.name,
                                                                    )
                                                                }
                                                                className="flex w-full items-center justify-between truncate pr-0"
                                                            >
                                                                <div className="flex items-center truncate">
                                                                    <span className="truncate">
                                                                        {
                                                                            item.label
                                                                        }
                                                                    </span>
                                                                    {item.hasSparkles && (
                                                                        <Sparkles className="ml-1 size-4" />
                                                                    )}
                                                                </div>
                                                            </Link>
                                                        </SidebarMenuSubButton>
                                                    )}
                                                </SidebarMenuSubItem>
                                            ))}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            )}
                        </SidebarMenuItem>
                    </Collapsible>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    )
}
